// A small lifecycle wrapper around the Screen Wake Lock API. One instance can
// track several simultaneous playback sources (for example overlapping Sips)
// and releases the browser sentinel only after the final source stops.
export default class ScreenWakeLock {
  constructor({ element = null, documentRef = globalThis.document, navigatorRef = globalThis.navigator } = {}) {
    this.element = element
    this.document = documentRef
    this.navigator = navigatorRef
    this.sources = new Set()
    this.sentinel = null
    this.pendingRequest = null
    this.disconnected = false

    this.onVisibilityChange = () => {
      if (this.documentHidden()) {
        void this.releaseSentinel()
      } else if (this.active) {
        void this.request()
      }
    }
    this.document?.addEventListener?.("visibilitychange", this.onVisibilityChange)
    this.setState(this.supported ? "idle" : "unsupported")
  }

  get active() {
    return !this.disconnected && this.sources.size > 0
  }

  get supported() {
    return typeof this.navigator?.wakeLock?.request === "function"
  }

  bindMedia(media, options = undefined) {
    if (!media || typeof media.addEventListener !== "function") return

    media.addEventListener("playing", () => this.hold(media), options)
    ;["pause", "ended", "error", "emptied", "abort"].forEach(eventName => {
      media.addEventListener(eventName, () => this.release(media), options)
    })
  }

  hold(source) {
    if (this.disconnected || !source) return

    this.sources.add(source)
    void this.request()
  }

  release(source) {
    if (!source) return

    this.sources.delete(source)
    if (!this.active) void this.releaseSentinel()
  }

  releaseAll() {
    this.sources.clear()
    void this.releaseSentinel()
  }

  disconnect() {
    if (this.disconnected) return

    this.disconnected = true
    this.document?.removeEventListener?.("visibilitychange", this.onVisibilityChange)
    this.sources.clear()
    void this.releaseSentinel()
  }

  request() {
    if (!this.supported) {
      this.setState("unsupported")
      return Promise.resolve(null)
    }
    if (!this.active || this.documentHidden()) return Promise.resolve(null)
    if (this.sentinel && !this.sentinel.released) return Promise.resolve(this.sentinel)
    if (this.pendingRequest) return this.pendingRequest

    let browserRequest
    try {
      browserRequest = this.navigator.wakeLock.request("screen")
    } catch (error) {
      this.markUnavailable(error)
      return Promise.resolve(null)
    }

    const pending = Promise.resolve(browserRequest)
      .then(sentinel => this.acceptSentinel(sentinel))
      .catch(error => {
        this.markUnavailable(error)
        return null
      })
      .finally(() => {
        if (this.pendingRequest === pending) this.pendingRequest = null
      })

    this.pendingRequest = pending
    return pending
  }

  acceptSentinel(sentinel) {
    if (!sentinel) return null
    if (!this.active || this.documentHidden()) {
      if (sentinel.released || typeof sentinel.release !== "function") return null
      return Promise.resolve(sentinel.release()).catch(() => {}).then(() => null)
    }

    this.sentinel = sentinel
    this.setState("active")
    sentinel.addEventListener?.("release", () => {
      if (this.sentinel !== sentinel) return
      this.sentinel = null
      this.setState(this.active ? "suspended" : this.inactiveState)
    }, { once: true })
    return sentinel
  }

  releaseSentinel() {
    const sentinel = this.sentinel
    this.sentinel = null
    this.setState(this.active ? "suspended" : this.inactiveState)
    if (!sentinel || sentinel.released || typeof sentinel.release !== "function") {
      return Promise.resolve()
    }

    return Promise.resolve(sentinel.release()).catch(error => {
      console.debug("Screen wake lock release failed", error)
    })
  }

  markUnavailable(error) {
    this.setState("unavailable")
    console.debug("Screen wake lock unavailable", error)
  }

  documentHidden() {
    return this.document?.visibilityState === "hidden" || this.document?.hidden === true
  }

  get inactiveState() {
    return this.supported ? "idle" : "unsupported"
  }

  setState(state) {
    if (this.element?.dataset) this.element.dataset.juiceboxWakeLock = state
  }
}
